import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../style/css/bootstrap.min.css';
import "../style/viewProduct.css";
import { addToCart } from './cartUtils';

const ProductCard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [itemQuantity, setItemQuantity] = useState(1);
  const [selectedVolume, setSelectedVolume] = useState('large');
  const [prices, setPrices] = useState([0]);

  const product = location.state?.product;
  const categoryName = location.state?.categoryName;

  // Extract and parse prices
  useEffect(() => {
    if (!product?.price) return;

    const priceString = product.price.toString();
    const priceValues = priceString.split(':').map(price => parseInt(price.trim()) || 0);
    
    if (priceValues.length > 1) {
      // Sort prices: small to large
      const sortedPrices = [...priceValues].sort((a, b) => a - b);
      setPrices(sortedPrices);
    } else {
      setPrices(priceValues);
    }
  }, [product]);

  const hasVariants = prices.length > 1;

  // Calculate current price based on selected volume
  const getCurrentPrice = () => {
    if (!hasVariants) return prices[0];
    
    return selectedVolume === 'large' ? prices[prices.length - 1] : prices[0];
  };

  const handleIncrement = () => {
    setItemQuantity(prev => prev + 1);
  };

  const handleDecrement = () => {
    if (itemQuantity > 1) {
      setItemQuantity(prev => prev - 1);
    }
  };

  const handleVolumeChange = (volume) => {
    setSelectedVolume(volume);
  };

  const handleAddToCart = () => {
    const cartItem = {
      id: product.id,
      name: product.name,
      price: getCurrentPrice(),
      quantity: itemQuantity,
      category: categoryName,
      image: product.image,
      volume: hasVariants ? selectedVolume : ""
    };
    
    addToCart(cartItem, itemQuantity);
    
    setItemQuantity(1);
    alert('تم إضافة المنتج إلى السلة بنجاح! 🎉');
    navigate('/');
  };

  const handleBackToMenu = () => {
    navigate(-1);
  };

  // Format price for display
  const formatPrice = (price) => {
    return `${price} جنيه`;
  };

  return (
    <div className="container" id='ViewProduct' style={{ marginTop: '150px' }}>
      {/* Back Button */}
      <button 
        onClick={handleBackToMenu}
        className="btn btn-outline-secondary mb-4"
        style={{ fontFamily: 'Tajawal, sans-serif' }}
      >
        ← العودة إلى القائمة
      </button>

      <div className="card product-card shadow-lg border-0">
        <div className="row g-0">
          {/* Left Side - Image */}
          {product.image && (
            <div className="col-md-6">
              <div className="product-image-container">
                <img 
                  src={product.image} 
                  className="img-fluid rounded-start h-100 object-fit-cover" 
                  alt={product.name}
                  style={{ objectFit: 'cover', height: '400px', width: '100%' }}
                />
              </div>
            </div>
          )}

          {/* Right Side - Content */}
          <div className="col-md-6">
            <div className="card-body h-100 d-flex flex-column justify-content-between py-4">
              
              {/* Category */}
              <div className="category-section mb-3">
                <span className="badge bg-primary fs-6 px-3 py-2">
                  📦 المجموعة: <span className="fw-normal">{categoryName}</span>
                </span>
              </div>

              {/* Product Name */}
              <h2 className="card-title fw-bold text-dark mb-3 fs-1">
                {product.name}
              </h2>

              {/* Price Display */}
              <div className="price-section mb-4">
                <h3 className="text-success fw-bold fs-2">
                  {hasVariants ? (
                    <>
                      {formatPrice(prices[0])} - {formatPrice(prices[1])}
                    </>
                  ) : (
                    formatPrice(prices[0])
                  )}
                </h3>
                {hasVariants && (
                  <p className="text-muted small mb-0">
                    السعر الحالي: {formatPrice(getCurrentPrice())}
                  </p>
                )}
              </div>

              {/* Volume Selection */}
              {hasVariants && (
                <div className="volume-section mb-4">
                  <label className="form-label fw-semibold text-secondary mb-2">
                    اختر الحجم
                  </label>
                  <div className="d-flex align-items-center gap-4">
                    {[
                      { value: 'small', label: 'صغير', price: prices[0] },
                      { value: 'large', label: 'كبير', price: prices[1] }
                    ].map((size) => (
                      <div key={size.value} className="form-check">
                        <input
                          type="radio"
                          name="volume"
                          id={size.value}
                          value={size.value}
                          checked={selectedVolume === size.value}
                          onChange={() => handleVolumeChange(size.value)}
                          className="form-check-input"
                          style={{ width: '20px', height: '20px' }}
                        />
                        <label 
                          htmlFor={size.value} 
                          className="form-check-label ms-2"
                        >
                          {size.label} ({formatPrice(size.price)})
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quantity Controls */}
              <div className="quantity-section mb-4">
                <label className="form-label fw-semibold text-secondary mb-2">
                  الكمية
                </label>
                <div className="d-flex align-items-center">
                  <button 
                    className="btn btn-outline-secondary rounded-circle"
                    onClick={handleDecrement}
                    style={{ width: '45px', height: '45px' }}
                    aria-label="تقليل الكمية"
                  >
                    -
                  </button>
                  
                  <input 
                    type="number" 
                    className="form-control text-center mx-2"
                    value={itemQuantity}
                    onChange={(e) => {
                      const value = Math.max(1, parseInt(e.target.value) || 1);
                      setItemQuantity(value);
                    }}
                    min="1"
                    style={{ width: '70px', height: '45px' }}
                    aria-label="الكمية"
                  />
                  
                  <button 
                    className="btn btn-outline-secondary rounded-circle"
                    onClick={handleIncrement}
                    style={{ width: '45px', height: '45px' }}
                    aria-label="زيادة الكمية"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Add to Cart Button */}
              <div className="action-section">
                <button 
                  className="btn btn-success btn-lg w-100 py-3 fw-bold fs-5"
                  onClick={handleAddToCart}
                  disabled={!product}
                >
                  🛒 أضف إلى السلة
                </button>
                
                {/* Additional Info */}
                <div className="product-info mt-3">
                  <div className="d-flex justify-content-between text-muted small">
                    <span>✅ توصيل سريع</span>
                    <span>✅ ضمان الجودة</span>
                    <span>✅ طازج يومياً</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;